// client/src/SatellitesPage.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import './SatellitesPage.css';

const API_ORIGIN = process.env.REACT_APP_API_URL;
const API_BASE = API_ORIGIN ? `${API_ORIGIN}/api` : '/api';

function authHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Надёжный base64url decode для JWT payload
 */
function decodeBase64UrlToJson(base64Url) {
  try {
    let b64 = String(base64Url || '').replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4 !== 0) b64 += '=';
    const json = atob(b64);
    return JSON.parse(json);
  } catch (e) {
    return null;
  }
}

/**
 * Достаём userId из JWT.
 * Поддерживаем разные варианты: id / userId / sub
 */
function getMyUserIdFromToken() {
  const token = localStorage.getItem('token');
  if (!token) return null;

  const parts = token.split('.');
  if (parts.length < 2) return null;

  const payload = decodeBase64UrlToJson(parts[1]);
  if (!payload) return null;

  return payload?.id ?? payload?.userId ?? payload?.sub ?? null;
}

function statusRu(s) {
  if (s === 'registration') return 'регистрация';
  if (s === 'running') return 'идёт';
  if (s === 'finished') return 'прошёл';
  if (s === 'cancelled') return 'отменён';
  return s || '';
}

function matchDesc(matchPoints, cubeEnabled) {
  const mp = Number(matchPoints || 0);
  if (!mp) return '—';

  // Только для "до 2" без куба
  if (mp === 2 && !cubeEnabled) return 'до 2 без куба';

  return `до ${mp} очков ${cubeEnabled ? 'с кубом' : ''}`.trim();
}

function fmtMoney(x) {
  const n = Number(x || 0);
  if (!Number.isFinite(n)) return '0';
  return String(Math.round(n));
}

function normalizeTg(u) {
  return String(u || '').trim().replace(/^@+/, '');
}

function isValidTg(u) {
  return /^[a-zA-Z0-9_]{5,32}$/.test(u);
}

/**
 * Красивый "золотой" кубок (без useId, чтобы работало и на React 17)
 */
function TrophySvg({ size = 22, className = '' }) {
  const gradIdRef = useRef(`trophy_grad_${Math.random().toString(16).slice(2)}`);
  const gradId = gradIdRef.current;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden="true"
      className={className}
      style={{ display: 'block' }}
    >
      <defs>
        <linearGradient id={gradId} x1="4" y1="3" x2="20" y2="21">
          <stop offset="0" stopColor="#fff1b8" />
          <stop offset="0.35" stopColor="#ffd889" />
          <stop offset="0.7" stopColor="#f2c25b" />
          <stop offset="1" stopColor="#b67a14" />
        </linearGradient>
      </defs>

      {/* Чаша */}
      <path
        d="M7 4h10v4a5 5 0 0 1-10 0V4z"
        fill={`url(#${gradId})`}
        stroke="rgba(0,0,0,0.45)"
        strokeWidth="1"
        strokeLinejoin="round"
      />

      {/* Ручки */}
      <path
        d="M7 5H4v3a4 4 0 0 0 4 4"
        fill="none"
        stroke="rgba(255,216,137,0.95)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M17 5h3v3a4 4 0 0 1-4 4"
        fill="none"
        stroke="rgba(255,216,137,0.95)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Ножка */}
      <path
        d="M12 12v4"
        fill="none"
        stroke="rgba(255,216,137,0.95)"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M9 20h6"
        fill="none"
        stroke="rgba(255,216,137,0.95)"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M10 16h4"
        fill="none"
        stroke="rgba(255,216,137,0.95)"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

export default function SatellitesPage() {
  const token = localStorage.getItem('token');
  const isAuthed = !!token;

  const myUsername = localStorage.getItem('username') || '';
  const myUserId = useMemo(() => getMyUserIdFromToken(), [token]);

  const [tab, setTab] = useState('registration');

  const [items, setItems] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [listError, setListError] = useState('');

  // превью игроков по турнирам (для карточек) — только для авторизованных
  const [playersByTid, setPlayersByTid] = useState({});
  const [matchesByTid, setMatchesByTid] = useState({});

  // модалка
  const [openId, setOpenId] = useState(null);
  const [openSat, setOpenSat] = useState(null);

  const [players, setPlayers] = useState([]);
  const [loadingPlayers, setLoadingPlayers] = useState(false);
  const [playersError, setPlayersError] = useState('');

  const [matches, setMatches] = useState([]);
  const [canEditMatches, setCanEditMatches] = useState(false);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [matchesError, setMatchesError] = useState('');

  // форма регистрации
  const [lastName, setLastName] = useState('');
  const [telegram, setTelegram] = useState('');
  const [password, setPassword] = useState('');

  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');

  const [cardBusyId, setCardBusyId] = useState(null);
  // редактирование результатов (в модалке)
  const [editScores, setEditScores] = useState({});

  const isModalOpen = openId != null;

  // защита от параллельных loadList
  const listLoadingRef = useRef(false);
  // защита от "устаревших" ответов loadPlayers/refresh
  const modalPlayersReqRef = useRef(0);

  // ✅ порядок действий (register/unregister): последнее действие побеждает
  const actionSeqRef = useRef(0);
  // ✅ чтобы по возможности отменять запрос регистрации, если сразу нажали "отменить"
  const registerAbortRef = useRef(null);

  const myRegistered = useMemo(() => {
    if (!isAuthed || !myUserId || !openId) return false;
    const list = playersByTid[openId] || players;
    return list.some((p) => String(p.userId) === String(myUserId));
  }, [playersByTid, players, myUserId, openId, isAuthed]);

  const visibleItems = useMemo(() => items, [items]);

  const longItems = useMemo(() => {
    return visibleItems.filter((s) => String(s.game_type || '').toLowerCase() === 'long');
  }, [visibleItems]);

  const shortItems = useMemo(() => {
    return visibleItems.filter((s) => String(s.game_type || '').toLowerCase() === 'short');
  }, [visibleItems]);

  function openTelegram(linkOrUsername) {
    if (!linkOrUsername) return;

    const raw = String(linkOrUsername).trim();

    if (raw.startsWith('http://') || raw.startsWith('https://')) {
      window.open(raw, '_blank', 'noopener,noreferrer');
      return;
    }

    const u = normalizeTg(raw);
    if (!isValidTg(u)) {
      console.warn('[TG] invalid username:', raw);
      return;
    }

    window.open(`https://t.me/${u}`, '_blank', 'noopener,noreferrer');
  }

  async function loadList(nextTab = tab, opts = {}) {
    const silent = !!opts.silent;

    if (listLoadingRef.current) return;
    listLoadingRef.current = true;

    if (!silent) setLoadingList(true);
    setListError('');

    try {
      const res = await axios.get(`${API_BASE}/satellites`, {
        params: { status: nextTab },
        headers: authHeaders(),
      });

      const arr = Array.isArray(res.data?.items) ? res.data.items : [];
      setItems(arr);

      // превью игроков/матчей — только если авторизован
      if (isAuthed) {
        await loadCardsPreview(arr);
      } else {
        setPlayersByTid({});
        setMatchesByTid({});
      }
    } catch (e) {
      console.error('[SatellitesPage] load failed', e);
      setListError(e?.response?.data?.error || 'Ошибка загрузки списка');

      if (!silent) {
        setItems([]);
        setPlayersByTid({});
        setMatchesByTid({});
      }
    } finally {
      if (!silent) setLoadingList(false);
      listLoadingRef.current = false;
    }
  }

  async function loadCardsPreview(arr) {
    const currentSeq = actionSeqRef.current;
    if (!Array.isArray(arr) || arr.length === 0) {
      setPlayersByTid({});
      setMatchesByTid({});
      return;
    }

    // игроки
    try {
      const playersPairs = await Promise.all(
        arr.map(async (sat) => {
          const r = await axios.get(`${API_BASE}/satellites/${sat.id}/players`, {
            headers: authHeaders(),
          });
          return [sat.id, Array.isArray(r.data?.players) ? r.data.players : []];
        })
      );

      const map = {};
      for (const [id, pls] of playersPairs) map[id] = pls;

      if (currentSeq !== actionSeqRef.current) return;
      setPlayersByTid(map);
    } catch (e) {
      console.error('[SatellitesPage] preview players failed', e);
      setPlayersByTid({});
    }

    // матчи (только running/finished)
    try {
      const need = arr.filter((s) => ['running', 'finished'].includes(String(s.status)));
      if (need.length === 0) {
        setMatchesByTid({});
        return;
      }

      const matchPairs = await Promise.all(
        need.map(async (sat) => {
          const r = await axios.get(`${API_BASE}/satellites/${sat.id}/matches`, {
            headers: authHeaders(),
          });
          return [
            sat.id,
            {
              matches: Array.isArray(r.data?.matches) ? r.data.matches : [],
              canEdit: !!r.data?.canEdit,
            },
          ];
        })
      );

      const map = {};
      for (const [id, val] of matchPairs) map[id] = val;
      setMatchesByTid(map);
    } catch (e) {
      console.error('[SatellitesPage] preview matches failed', e);
      setMatchesByTid({});
    }
  }

  async function loadPlayers(tournamentId) {
    if (!isAuthed) return;

    const reqId = ++modalPlayersReqRef.current;

    setLoadingPlayers(true);
    setPlayersError('');

    try {
      const res = await axios.get(`${API_BASE}/satellites/${tournamentId}/players`, {
        headers: authHeaders(),
        params: { _ts: Date.now() },
      });

      if (reqId !== modalPlayersReqRef.current) return;

      setPlayers(Array.isArray(res.data?.players) ? res.data.players : []);
    } catch (e) {
      if (reqId !== modalPlayersReqRef.current) return;

      console.error('[SatellitesPage] load players failed', e);
      setPlayersError(e?.response?.data?.error || 'Ошибка загрузки игроков');
      setPlayers([]);
    } finally {
      if (reqId === modalPlayersReqRef.current) setLoadingPlayers(false);
    }
  }

  async function loadMatches(tournamentId) {
    if (!isAuthed) return;

    setLoadingMatches(true);
    setMatchesError('');

    try {
      const res = await axios.get(`${API_BASE}/satellites/${tournamentId}/matches`, {
        headers: authHeaders(),
      });

      const arr = Array.isArray(res.data?.matches) ? res.data.matches : [];
      setMatches(arr);
      setCanEditMatches(!!res.data?.canEdit);

      // подготовим editScores
      const m = {};
      for (const x of arr) {
        m[x.id] = {
          score_p1: String(x.score_p1 ?? ''),
          score_p2: String(x.score_p2 ?? ''),
        };
      }
      setEditScores(m);
    } catch (e) {
      console.error('[SatellitesPage] load matches failed', e);
      setMatchesError(e?.response?.data?.error || 'Ошибка загрузки матчей');
      setMatches([]);
      setCanEditMatches(false);
      setEditScores({});
    } finally {
      setLoadingMatches(false);
    }
  }

  // тихо обновляет игроков конкретного турнира
  async function refreshTournamentPlayers(tournamentId, { updateModal = false } = {}) {
    if (!isAuthed) return;

    const reqId = ++modalPlayersReqRef.current;

    try {
      const res = await axios.get(`${API_BASE}/satellites/${tournamentId}/players`, {
        headers: authHeaders(),
        params: { _ts: Date.now() },
      });

      if (reqId !== modalPlayersReqRef.current) return;

      const freshPlayers = Array.isArray(res.data?.players) ? res.data.players : [];

      setPlayersByTid((prev) => ({ ...prev, [tournamentId]: freshPlayers }));

      setItems((prev) =>
        prev.map((s) =>
          String(s.id) === String(tournamentId) ? { ...s, playersCount: freshPlayers.length } : s
        )
      );

      if (updateModal) setPlayers(freshPlayers);
    } catch (e) {
      if (reqId !== modalPlayersReqRef.current) return;
      console.error('[SatellitesPage] refreshTournamentPlayers failed', e);
    }
  }

  // первичная загрузка + авто-обновление
  useEffect(() => {
    loadList(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, isAuthed]);

  useEffect(() => {
    const id = setInterval(() => {
      loadList(tab, { silent: true });
    }, 12000);

    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, isAuthed]);

  function openModal(sat) {
    setOpenId(sat.id);
    setOpenSat(sat);

    // сброс
    setPlayers([]);
    setPlayersError('');
    setMatches([]);
    setMatchesError('');
    setEditScores({});
    setActionError('');

    setLastName('');
    setTelegram('');
    setPassword('');

    if (isAuthed) {
      loadPlayers(sat.id);

      if (['running', 'finished'].includes(String(sat.status))) {
        loadMatches(sat.id);
      } else {
        setMatches([]);
        setCanEditMatches(false);
      }
    } else {
      setPlayers([]);
      setMatches([]);
      setCanEditMatches(false);
    }
  }

  function closeModal() {
    setOpenId(null);
    setOpenSat(null);
    setPlayers([]);
    setMatches([]);
    setPlayersError('');
    setMatchesError('');
    setActionError('');
    setEditScores({});
  }

  function nextActionSeq() {
    const now = Date.now();
    actionSeqRef.current = Math.max(actionSeqRef.current + 1, now);
    return actionSeqRef.current;
  }

  function isLatestAction(seq) {
    return seq === actionSeqRef.current;
  }

  async function doRegister() {
    if (!isAuthed) {
      setActionError('Чтобы зарегистрироваться, нужно войти в аккаунт.');
      return;
    }
    if (!openId) return;

    const ln = String(lastName || '').trim();
    const tg = String(telegram || '').trim();

    if (!ln) {
      setActionError('Заполни фамилию');
      return;
    }

    if (openSat?.password_required) {
      const p = String(password || '').trim();
      if (!p) {
        setActionError('Нужен пароль');
        return;
      }
    }

    const seq = nextActionSeq();

    // ✅ если прошлый запрос регистрации ещё идёт — отменим
    try {
      if (registerAbortRef.current) {
        registerAbortRef.current.abort();
      }
    } catch (_) {}

    const controller = new AbortController();
    registerAbortRef.current = controller;

    setActionLoading(true);
    setActionError('');

    try {
      await axios.post(
        `${API_BASE}/satellites/${openId}/register`,
        {
          seq,
          last_name: ln,
          telegram_username: tg ? tg : undefined,
          password: openSat?.password_required ? String(password || '').trim() : undefined,
        },
        {
          headers: authHeaders(),
          signal: controller.signal,
        }
      );

      await refreshTournamentPlayers(openId, { updateModal: true });

      if (!isLatestAction(seq)) return;

      await loadPlayers(openId);
      if (!isLatestAction(seq)) return;
    } catch (e) {
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') {
        return;
      }

      console.error('[SatellitesPage] register failed', e);

      if (isLatestAction(seq)) {
        setActionError(e?.response?.data?.error || 'Ошибка регистрации');
      }
    } finally {
      if (isLatestAction(seq)) {
        setActionLoading(false);
      }
    }
  }

  async function doUnregister() {
    if (!isAuthed) {
      setActionError('Чтобы отменить регистрацию, нужно войти в аккаунт.');
      return;
    }
    if (!openId) return;

    const seq = nextActionSeq();

    try {
      if (registerAbortRef.current) {
        registerAbortRef.current.abort();
        registerAbortRef.current = null;
      }
    } catch (_) {}

    // отменяем влияние любых старых ответов loadPlayers/refresh, которые ещё в полёте
    modalPlayersReqRef.current += 1;

    const tid = openId;

    const meId = myUserId != null ? String(myUserId) : '';
    const meName = String(myUsername || '').trim().toLowerCase();

    const filterMeOut = (p) => {
      if (meId) return String(p?.userId) !== meId;
      if (meName) return String(p?.username || '').toLowerCase() !== meName;
      return true;
    };

    // снимки для отката
    const prevModalPlayers = players;
    const prevCardPlayers = playersByTid[tid] || [];
    const prevItems = items;

    // ✅ оптимистично убираем из UI сразу
    setPlayers((prev) => prev.filter(filterMeOut));

    const baseList =
      prevCardPlayers && prevCardPlayers.length > 0 ? prevCardPlayers : prevModalPlayers;
    const optimisticPlayers = Array.isArray(baseList) ? baseList.filter(filterMeOut) : [];
    const optimisticCount = optimisticPlayers.length;

    setPlayersByTid((prev) => ({ ...prev, [tid]: optimisticPlayers }));

    setItems((prev) =>
      prev.map((s) => (String(s.id) === String(tid) ? { ...s, playersCount: optimisticCount } : s))
    );

    setActionLoading(true);
    setActionError('');

    try {
      await axios.post(`${API_BASE}/satellites/${tid}/unregister`, { seq }, { headers: authHeaders() });

      // очищаем форму
      setLastName('');
      setTelegram('');
      setPassword('');
      setActionError('');

      // подтягиваем игроков заново
      await loadPlayers(tid);
      if (!isLatestAction(seq)) return;
    } catch (e) {
      console.error('[SatellitesPage] unregister failed', e);

      // откат UI
      setPlayers(prevModalPlayers);
      setPlayersByTid((prev) => ({ ...prev, [tid]: prevCardPlayers }));
      setItems(prevItems);

      setActionError(e?.response?.data?.error || 'Ошибка отмены регистрации');
    } finally {
      if (isLatestAction(seq)) setActionLoading(false);
    }
  }




  // ✅ Отмена регистрации прямо с карточки (без открытия модалки)
async function doUnregisterFromCard(tid) {
  if (!isAuthed) {
    // гость — нечего отменять
    return;
  }
  if (!tid) return;

  const seq = nextActionSeq();

  // если где-то ещё висит запрос регистрации — отменим
  try {
    if (registerAbortRef.current) {
      registerAbortRef.current.abort();
      registerAbortRef.current = null;
    }
  } catch (_) {}

  setCardBusyId(tid);

  const meId = myUserId != null ? String(myUserId) : '';
  const meName = String(myUsername || '').trim().toLowerCase();

  const filterMeOut = (p) => {
    if (meId) return String(p?.userId) !== meId;
    if (meName) return String(p?.username || '').toLowerCase() !== meName;
    return true;
  };

  // снимки для отката
  const prevCardPlayers = playersByTid[tid] || [];
  const prevItems = items;

  // ✅ оптимистично убираем тебя из карточки сразу
  const optimisticPlayers = Array.isArray(prevCardPlayers) ? prevCardPlayers.filter(filterMeOut) : [];
  const optimisticCount = optimisticPlayers.length;

  setPlayersByTid((prev) => ({ ...prev, [tid]: optimisticPlayers }));

  setItems((prev) =>
    prev.map((s) => (String(s.id) === String(tid) ? { ...s, playersCount: optimisticCount } : s))
  );

  // если модалка открыта на этом же турнире — обновим и её список игроков
  const modalSame = openId != null && String(openId) === String(tid);
  if (modalSame) {
    setPlayers((prev) => prev.filter(filterMeOut));
  }

  try {
    await axios.post(`${API_BASE}/satellites/${tid}/unregister`, { seq }, { headers: authHeaders() });

    // подтянуть актуальных игроков с сервера (на всякий)
    await refreshTournamentPlayers(tid, { updateModal: modalSame });

    // если модалка открыта — обновим таблицу игроков точно
    if (modalSame) {
      await loadPlayers(tid);
    }
  } catch (e) {
    console.error('[SatellitesPage] card unregister failed', e);

    // откат
    setPlayersByTid((prev) => ({ ...prev, [tid]: prevCardPlayers }));
    setItems(prevItems);

    // если модалка открыта — там покажем ошибку
    if (modalSame) {
      setActionError(e?.response?.data?.error || 'Ошибка отмены регистрации');
    }
  } finally {
    if (isLatestAction(seq)) {
      setCardBusyId(null);
    }
  }
}



  function onEditScore(matchId, field, value) {
    setEditScores((prev) => ({
      ...prev,
      [matchId]: {
        ...(prev[matchId] || { score_p1: '', score_p2: '' }),
        [field]: value,
      },
    }));
  }

  async function saveMatchResult(matchId) {
    if (!isAuthed) return;
    if (!openId) return;

    const v = editScores[matchId] || { score_p1: '', score_p2: '' };
    const s1 = Number(String(v.score_p1).trim());
    const s2 = Number(String(v.score_p2).trim());

    const match = matches.find((x) => String(x.id) === String(matchId));
    const isFinal = String(match?.round) === 'final';

    if (!Number.isFinite(s1) || !Number.isFinite(s2)) {
      setMatchesError('Счёт должен быть числами');
      return;
    }

    if (s1 < 0 || s2 < 0) {
      setMatchesError('Счёт не может быть отрицательным');
      return;
    }

    if (s1 === 0 && s2 === 0) {
      setMatchesError('Счёт не может быть 0:0');
      return;
    }

    // ✅ Ничья разрешена ТОЛЬКО в финале
    if (s1 === s2 && !isFinal) {
      setMatchesError('Ничья запрещена (кроме финала)');
      return;
    }

    setActionLoading(true);
    setMatchesError('');

    try {
      await axios.post(
        `${API_BASE}/satellites/${openId}/matches/${matchId}/result`,
        { score_p1: s1, score_p2: s2 },
        { headers: authHeaders() }
      );

      await loadMatches(openId);
      await loadList(tab, { silent: true });
    } catch (e) {
      console.error('[SatellitesPage] save result failed', e);
      setMatchesError(e?.response?.data?.error || 'Ошибка сохранения результата');
    } finally {
      setActionLoading(false);
    }
  }



 



  function renderCard(sat) {

    const gt = String(sat?.game_type ?? '').toLowerCase();
const isLongType = gt === 'long';
const isShortType = gt === 'short';

const cardTypeClass = isLongType ? 'sat-card--long' : isShortType ? 'sat-card--short' : '';
const typePillClass = isLongType ? 'sat-typePill--long' : isShortType ? 'sat-typePill--short' : '';



    const maxPlayers = Number(sat.maxPlayers ?? sat.max_players ?? 4);
    const previewPlayers = playersByTid[sat.id] || [];
    const regCount = Number(sat.playersCount ?? previewPlayers.length ?? 0);

    const buyIn = sat.buy_in ?? 0;
    const prize = sat.prize ?? 0;
    const matchPoints = sat.match_points ?? 0;
    const cubeEnabled = !!sat.cube_enabled;
    const passwordRequired = !!sat.password_required;

    const previewMatches = matchesByTid[sat.id]?.matches || [];

    // игроки 1..4 по seat_no если есть, иначе по порядку
    const seatMap = new Map();
    for (const p of previewPlayers) {
      const seat = Number(p.seat_no);
      if (Number.isFinite(seat) && seat >= 1 && seat <= 4) seatMap.set(seat, p);
    }

    const listOrdered = [];
    for (let i = 1; i <= 4; i++) {
      if (seatMap.has(i)) listOrdered.push(seatMap.get(i));
    }
    for (const p of previewPlayers) {
      if (!listOrdered.includes(p)) listOrdered.push(p);
    }

    function getPlayerLine(i) {
      const p = listOrdered[i - 1];
      if (!p) return null;

      const uname = p.username || '—';
      const isMe = myUsername && String(uname).toLowerCase() === String(myUsername).toLowerCase();
      const tg = p.telegramLink || p.telegram_username || '';
      return { uname, isMe, tg };
    }

    return (
     <div key={sat.id} className={`sat-card ${cardTypeClass}`}>
        <div className="sat-card__top">
          <h3 className="sat-card__name">{sat.title || `Сателлит #${sat.id}`}</h3>

        </div>

        <div className="sat-meta">
          <div className="sat-row">
            <span className="k">взнос</span>
            <span className="v">{fmtMoney(buyIn)} Руб</span>
          </div>
          <div className="sat-row">
            <span className="k">приз</span>
            <span className="v">{fmtMoney(prize)} Руб</span>
          </div>
          <div className="sat-row">
            <span className="k">матч</span>
            <span className="v">{matchDesc(matchPoints, cubeEnabled)}</span>
          </div>
          <div className="sat-row">
            <span className="k">пароль</span>
            <span className="v">{passwordRequired ? 'нужен' : 'не нужен'}</span>
          </div>

          <div className="sat-row sat-row--seats">
            <span className="k">места</span>
            <span className="v">
              {regCount}/{maxPlayers}
            </span>
          </div>
        </div>

        {/* игроки прямо в карточке (у гостя будет пусто — нормально) */}
        <div className="sat-card__players">
          {Array.from({ length: 4 }, (_, idx) => {
            const n = idx + 1;
            const pl = getPlayerLine(n);

            if (!pl) {
              return (
                <div className="sat-player sat-player--empty" key={`empty-${sat.id}-${n}`}>
                  <span className="sat-player__num">{n}.</span>
                  <span className="sat-player__name">—</span>
                </div>
              );
            }

            return (
              <div className={`sat-player ${pl.isMe ? 'sat-player--me' : ''}`} key={`p-${sat.id}-${n}`}>
                <span className="sat-player__num">{n}.</span>

                <button
                  type="button"
                  className="sat-link sat-player__name"
                  onClick={(e) => {
                    e.stopPropagation();
                    openTelegram(pl.tg);
                  }}
                  disabled={!pl.tg}
                  title={pl.tg ? 'Открыть Telegram' : 'Telegram не указан'}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    color: 'inherit',
                    font: 'inherit',
                    cursor: pl.tg ? 'pointer' : 'default',
                    textAlign: 'left',
                  }}
                >
                  {pl.uname}
                </button>

                {pl.isMe ? <span className="sat-player__me">вы</span> : null}
              </div>
            );
          })}

          {/* пары/матчи */}
          {['running', 'finished'].includes(String(sat.status)) && previewMatches.length > 0 ? (
            <div style={{ marginTop: 8, fontSize: 25, opacity: 0.95 }}>
              <div style={{ fontWeight: 900, marginBottom: 4 }}>пары</div>
              {previewMatches.map((m) => {
                const left = m?.p1?.username || '—';
                const right = m?.p2?.username || '—';
                const score = m.status === 'finished' ? `${m.score_p1}:${m.score_p2}` : '—';
                const title = m.round === 'final' ? 'финал' : `матч ${m.match_no}`;
                return (
                  <div key={m.id} style={{ opacity: 0.9, lineHeight: 1.25 }}>
                    {title}: {left} — {right} ({score})
                  </div>
                );
              })}
            </div>
          ) : null}



          {String(sat.status) === 'finished' && previewMatches.length > 0 ? (() => {
  const finalMatch = previewMatches.find((m) => String(m.round) === 'final');
  if (!finalMatch || String(finalMatch.status) !== 'finished') return null;

  const a = Number(finalMatch.score_p1);
  const b = Number(finalMatch.score_p2);
  const isDraw = Number.isFinite(a) && Number.isFinite(b) && a === b;

  const winner = finalMatch?.winner?.username ? String(finalMatch.winner.username) : '';

  return (
    <div className="sat-card__winner">
      <span className="sat-card__winnerIcons">
        {isDraw ? (
          <>
            <TrophySvg className="sat-trophyIcon" size={20} />
            <TrophySvg className="sat-trophyIcon" size={20} />
          </>
        ) : (
          <TrophySvg className="sat-trophyIcon" size={22} />
        )}
      </span>

      <span className="sat-card__winnerText">
        {isDraw ? (
          'Ничья в финале'
        ) : winner ? (
          <>
            Победитель: <span className="sat-card__winnerName">{winner}</span>
          </>
        ) : (
          'Финал завершён'
        )}
      </span>
    </div>
  );
})() : null}

        </div>

        <div className="sat-actions">
        <div className="sat-actions">
  {(() => {
    const isReg = String(sat.status) === 'registration';

    // ✅ я зарегистрирован в этом турнире?
    const iAmRegisteredHere =
      isAuthed &&
      myUserId != null &&
      (playersByTid[sat.id] || []).some((p) => String(p.userId) === String(myUserId));

    const showCancel = isReg && iAmRegisteredHere;
    const busy = cardBusyId != null && String(cardBusyId) === String(sat.id);


    const btnClass = [
  'sat-open',
  isReg ? (showCancel ? 'sat-open--cancel' : 'sat-open--reg') : '',
  busy ? 'is-busy' : '',
].filter(Boolean).join(' ');

    return (
       <button
    className={btnClass}
    type="button"
    disabled={busy}
    onClick={() => {
      if (showCancel) {
        doUnregisterFromCard(sat.id);
      } else {
        openModal(sat);
      }
    }}
  >
    {isReg ? (showCancel ? (busy ? 'Отмена...' : 'Отмена') : 'Регистрация') : 'Смотреть'}
  </button>
);
  })()}
</div>

        </div>
      </div>
    );
   

  }

  return (
    <div className="satellites-page">
      <div className="satellites-container">
        {/* <div className="satellites-head">
          <h2 className="satellites-title">Сателлиты</h2>
        </div> */}

        <div className="satellites-filters">
          <div className="satellites-tabs">
            <button
              className={`sat-tab sat-tab--registration ${tab === 'registration' ? 'is-active' : ''}`}
              onClick={() => setTab('registration')}
              type="button"
            >
              регистрация
            </button>

            <button
              className={`sat-tab sat-tab--running ${tab === 'running' ? 'is-active' : ''}`}
              onClick={() => setTab('running')}
              type="button"
            >
              идут
            </button>

            <button
              className={`sat-tab sat-tab--finished ${tab === 'finished' ? 'is-active' : ''}`}
              onClick={() => setTab('finished')}
              type="button"
            >
              прошедшие
            </button>
          </div>
        </div>

        {listError ? <div className="sat-empty">{listError}</div> : null}

        {loadingList ? (
          <div className="sat-empty">Загрузка...</div>
        ) : (
          <>
            <div className="sat-gridWrap">
              {/* ДЛИННЫЕ */}
              {longItems.length > 0 ? <div className="sat-grid">{longItems.map((sat) => renderCard(sat))}</div> : null}

              {/* небольшой отступ между сетками */}
              {longItems.length > 0 && shortItems.length > 0 ? <div style={{ height: 18 }} /> : null}

              {/* КОРОТКИЕ */}
              {shortItems.length > 0 ? <div className="sat-grid">{shortItems.map((sat) => renderCard(sat))}</div> : null}
            </div>

            {longItems.length === 0 && shortItems.length === 0 ? <div className="sat-empty">Пока ничего нет</div> : null}
          </>
        )}

        {/* MODAL */}
        {isModalOpen ? (
          <div className="sat-modal__backdrop" onMouseDown={closeModal} role="presentation">
            <div className="sat-modal" onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
              <div className="sat-modal__head">
                <div className="sat-modal__title">
                  {openSat?.title || `Сателлит #${openId}`}
                  <span className="sat-modal__status" style={{ marginLeft: 10, opacity: 0.8 }}>
                    {statusRu(openSat?.status)}
                  </span>
                </div>

                <div className="sat-modal__headActions" style={{ display: 'flex', gap: 8 }}>
                  <button
                    className="sat-modal__iconBtn"
                    type="button"
                    title="Обновить"
                    disabled={!isAuthed || actionLoading || loadingPlayers}
                    onClick={() => {
                      if (!openId) return;
                      refreshTournamentPlayers(openId, { updateModal: true });
                    }}
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 10,
                      border: '1px solid rgba(255,255,255,0.14)',
                      background: 'rgba(255,255,255,0.06)',
                      color: 'inherit',
                      cursor: !isAuthed ? 'not-allowed' : 'pointer',
                      fontSize: 16,
                    }}
                  >
                    ⟳
                  </button>

                  <button
                    className="sat-modal__iconBtn sat-modal__iconBtn--close"
                    onClick={closeModal}
                    type="button"
                    title="Закрыть"
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 10,
                      border: '1px solid rgba(255,255,255,0.14)',
                      background: 'rgba(255,255,255,0.06)',
                      color: 'inherit',
                      cursor: 'pointer',
                      fontSize: 18,
                      lineHeight: 0,
                    }}
                  >
                    ×
                  </button>
                </div>
              </div>

              {/* ✅ один скролл внутри модалки */}
              <div className="sat-modal__body">
                {/* игроки */}
                <div className="sat-tableWrap">
                  {!isAuthed ? (
                    <div className="sat-empty">Войдите в аккаунт, чтобы видеть список игроков.</div>
                  ) : (
                    <>
                      {playersError ? <div className="sat-empty">{playersError}</div> : null}

                      {loadingPlayers ? (
                        <div className="sat-empty">Загрузка игроков...</div>
                      ) : (
                        <table className="sat-table">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>ник</th>
                              <th>фамилия</th>
                              <th>telegram</th>
                            </tr>
                          </thead>
                          <tbody>
                            {players.map((p, idx) => (
                              <tr key={p.id ?? `${p.username}-${idx}`}>
                                <td>{idx + 1}</td>
                                <td>
                                  <button
                                    className="sat-link"
                                    onClick={() => openTelegram(p.telegramLink || p.telegram_username)}
                                    disabled={!(p.telegramLink || p.telegram_username)}
                                    title={
                                      p.telegramLink || p.telegram_username ? 'Открыть Telegram' : 'Telegram не указан'
                                    }
                                    type="button"
                                  >
                                    {p.username}
                                  </button>

                                  {p.userId != null && myUserId != null && String(p.userId) === String(myUserId) ? (
                                    <span style={{ marginLeft: 8, fontWeight: 900, color: '#ffd889' }}>вы</span>
                                  ) : null}
                                </td>
                                <td>{p.last_name || '-'}</td>
                                <td>{p.telegram_username ? `@${String(p.telegram_username).replace(/^@+/, '')}` : '-'}</td>
                              </tr>
                            ))}

                            {players.length === 0 ? (
                              <tr>
                                <td colSpan="4" className="sat-emptyRow">
                                  Пока никого нет
                                </td>
                              </tr>
                            ) : null}
                          </tbody>
                        </table>
                      )}
                    </>
                  )}
                </div>

                {/* матчи */}
                {isAuthed && ['running', 'finished'].includes(String(openSat?.status)) ? (
                  <div className="sat-form" style={{ borderTop: '1px solid rgba(255,255,255,0.10)' }}>
                    <div style={{ fontWeight: 900, marginBottom: 8 }}>Матчи</div>

                    <div style={{ opacity: 0.85, fontWeight: 800, marginBottom: 8 }}>
                      Редактирование: {canEditMatches ? 'ДА' : 'НЕТ'}
                    </div>

                    {matchesError ? <div className="sat-error">{matchesError}</div> : null}

                    {loadingMatches ? (
                      <div className="sat-empty">Загрузка матчей...</div>
                    ) : matches.length === 0 ? (
                      <div className="sat-empty">Матчи ещё не созданы</div>
                    ) : (
                      <div className="sat-matchesGrid">
                        {matches.map((m) => {
                          const title = m.round === 'final' ? 'Финал' : `Полуфинал ${m.match_no}`;
                          const left = m?.p1?.username || '—';
                          const right = m?.p2?.username || '—';

                          const v = editScores[m.id] || {
                            score_p1: String(m.score_p1 ?? 0),
                            score_p2: String(m.score_p2 ?? 0),
                          };

                          const winnerName = String(m?.winner?.username || '').trim().toLowerCase();
                          const leftIsWinner = winnerName && String(left).toLowerCase() === winnerName;
                          const rightIsWinner = winnerName && String(right).toLowerCase() === winnerName;

                          return (
                            <div
                              key={m.id}
                              className={`sat-matchCard ${m.round === 'final' ? 'sat-matchCard--final' : 'sat-matchCard--semi'}`}
                              style={{
                                padding: 10,
                                border: '1px solid rgba(255,255,255,0.10)',
                                borderRadius: 12,
                              }}
                            >
                              <div className="sat-matchHead" style={{ fontWeight: 900, marginBottom: 6 }}>
                                <span className="sat-matchTitle">{title}</span>
                                <span
                                  className={`sat-roundPill ${
                                    m.round === 'final' ? 'sat-roundPill--final' : 'sat-roundPill--semi'
                                  }`}
                                >
                                  {m.round === 'final' ? 'главный матч' : 'отбор'}
                                </span>
                              </div>

                              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                <div className="sat-matchPlayers" style={{ minWidth: 220, fontWeight: 800, opacity: 0.95 }}>
                                  <span className={`sat-playerName ${leftIsWinner ? 'is-winner' : ''}`}>{left}</span>
                                  <span className="sat-vs">—</span>
                                  <span className={`sat-playerName ${rightIsWinner ? 'is-winner' : ''}`}>{right}</span>
                                </div>

                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <input
                                    className="sat-input"
                                    style={{ width: 80 }}
                                    value={v.score_p1}
                                    disabled={!canEditMatches || actionLoading}
                                    onChange={(e) => onEditScore(m.id, 'score_p1', e.target.value)}
                                  />
                                  <span style={{ fontWeight: 900 }}>:</span>
                                  <input
                                    className="sat-input"
                                    style={{ width: 80 }}
                                    value={v.score_p2}
                                    disabled={!canEditMatches || actionLoading}
                                    onChange={(e) => onEditScore(m.id, 'score_p2', e.target.value)}
                                  />
                                </div>

                                <div className="sat-statusPill">статус: {m.status}</div>

                                {canEditMatches ? (
                                  <button className="sat-btn" type="button" disabled={actionLoading} onClick={() => saveMatchResult(m.id)}>
                                    сохранить
                                  </button>
                                ) : null}
                              </div>

                              {m.status === 'finished' ? (
                                <div className="sat-finalResult">
                                  {(() => {
                                    const a = Number(m.score_p1);
                                    const b = Number(m.score_p2);
                                    const isDraw = Number.isFinite(a) && Number.isFinite(b) && a === b;

                                    // ✅ финал: можно ничью, красиво показываем
                                    if (m.round === 'final') {
                                      if (isDraw) {
                                        return (
                                          <>
                                            <span className="sat-trophyWrap" title="Ничья">
                                              <TrophySvg className="sat-trophyIcon" size={28} />
                                              <TrophySvg className="sat-trophyIcon" size={28} />
                                            </span>
                                            <span className="sat-finalText">ничья</span>
                                          </>
                                        );
                                      }

                                      return (
                                        <>
                                          <span className="sat-trophyWrap" title="Победитель">
                                            <TrophySvg className="sat-trophyIcon" size={30} />
                                          </span>
                                          {m.winner?.username ? (
                                            <span className="sat-finalText">
                                              победитель: <span className="sat-winnerName">{m.winner.username}</span>
                                            </span>
                                          ) : (
                                            <span className="sat-finalText">финал завершён</span>
                                          )}
                                        </>
                                      );
                                    }

                                    // полуфинал
                                    return m.winner?.username ? (
                                      <span className="sat-semiWinner">
                                        победитель: <span className="sat-winnerName">{m.winner.username}</span>
                                      </span>
                                    ) : (
                                      <span style={{ opacity: 0.85 }}>матч завершён</span>
                                    );
                                  })()}
                                </div>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ) : null}

                {/* Регистрация / отмена */}
                <div className="sat-form">
                  {!isAuthed ? (
                    <div className="sat-hint">Чтобы зарегистрироваться, нужно войти в аккаунт.</div>
                  ) : openSat && openSat.status !== 'registration' ? (
                    <div className="sat-hint">Регистрация закрыта (статус: {statusRu(openSat?.status)})</div>
                  ) : myRegistered ? (
                    <div className="sat-actions sat-actions--modal">
                      <div className="sat-hint">Вы уже зарегистрированы</div>

                      <button
                        type="button"
                        className="sat-btn sat-btn--danger"
                        disabled={actionLoading}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();

                          setActionLoading(true);
                          setTimeout(() => {
                            doUnregister();
                          }, 1500);
                        }}
                      >
                        отменить регистрацию
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="sat-formGrid">
                        <label className="sat-field">
                          <div className="sat-field__label">Фамилия *</div>
                          <input
                            className="sat-input"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            placeholder="Иванов"
                          />
                        </label>

                        <label className="sat-field">
                          <div className="sat-field__label">Telegram username </div>
                          <input
                            className="sat-input"
                            value={telegram}
                            onChange={(e) => setTelegram(e.target.value)}
                            placeholder="@nickname"
                          />
                        </label>

                        {openSat?.password_required ? (
                          <label className="sat-field">
                            <div className="sat-field__label">Пароль *</div>
                            <input
                              className="sat-input"
                              value={password}
                              onChange={(e) => setPassword(e.target.value)}
                              placeholder="пароль"
                            />
                          </label>
                        ) : null}
                      </div>

                      {actionError ? <div className="sat-error">{actionError}</div> : null}

                      <div className="sat-actions sat-actions--modal">
                        <button className="sat-btn sat-btn--primary" onClick={doRegister} disabled={actionLoading} type="button">
                          зарегистрироваться
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
              {/* конец sat-modal__body */}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
